import { useEffect, useState, lazy, Suspense } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { HeroSection } from "@/components/HeroSection";
import { AboutSection } from "@/components/AboutSection";
import { Footer } from "@/components/Footer";
import { ContactModal } from "@/components/ContactModal";
import { useClickTracking } from "@/hooks/useClickTracking";
import { supabase } from "@/integrations/supabase/client";
import { useAdmin } from "@/hooks/useAdmin";
import { useExcludedIP } from "@/hooks/useExcludedIP";
import { DebugOverlay } from "@/components/DebugOverlay";
import { SEO } from "@/components/SEO";

// Lazy load below-the-fold sections for better initial load performance
const TikTokFeed = lazy(() => import("@/components/TikTokFeed").then(m => ({ default: m.TikTokFeed })));
const ShopSection = lazy(() => import("@/components/ShopSection").then(m => ({ default: m.ShopSection })));
const InstagramFeed = lazy(() => import("@/components/InstagramFeed").then(m => ({ default: m.InstagramFeed })));
const VideoSection = lazy(() => import("@/components/VideoSection").then(m => ({ default: m.VideoSection })));
const BlogSection = lazy(() => import("@/components/BlogSection").then(m => ({ default: m.BlogSection })));
const NewsletterSection = lazy(() => import("@/components/NewsletterSection").then(m => ({ default: m.NewsletterSection })));

interface SectionConfig {
  section_name: string;
  order_position: number;
  is_visible: boolean;
}

const SECTION_COMPONENTS: Record<string, React.ComponentType> = {
  about: AboutSection,
  tiktok: TikTokFeed,
  instagram: InstagramFeed,
  videos: VideoSection,
  blog: BlogSection,
  newsletter: NewsletterSection,
  shop: ShopSection,
};

const Index = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trackClick } = useClickTracking();
  const { isAdmin, isLoading: isAdminLoading } = useAdmin();
  const { isExcluded: isExcludedIP, isChecking: isCheckingIP } = useExcludedIP();
  const [isContactOpen, setIsContactOpen] = useState(false);

  // Handle TikTok OAuth callback at root domain
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    // Check if this is a TikTok OAuth callback
    // TikTok redirects to root domain with code and state parameters
    if (code && state) {
      // Redirect to callback page to handle the token exchange
      const params = new URLSearchParams(window.location.search);
      navigate(`/tiktok/callback?${params.toString()}`, { replace: true });
    } else if (error) {
      // Handle OAuth error at root
      navigate(`/tiktok/callback?${window.location.search}`, { replace: true });
    }
  }, [searchParams, navigate]);
  // Default to safe visible sections - exclude TikTok/Instagram by default
  const [sections, setSections] = useState<SectionConfig[]>([
    { section_name: 'about', order_position: 1, is_visible: true },
    { section_name: 'videos', order_position: 4, is_visible: true },
    { section_name: 'blog', order_position: 5, is_visible: true },
    { section_name: 'newsletter', order_position: 6, is_visible: true },
  ]);

  // Handle scroll navigation when returning from blog posts
  useEffect(() => {
    // Only scroll if coming from a blog post (has location state)
    const state = location.state as { scrollTo?: string } | null;

    if (state?.scrollTo === 'blog') {
      // Wait for lazy-loaded sections to render, then scroll
      const timer = setTimeout(() => {
        const blogElement = document.getElementById('blog');
        if (blogElement) {
          blogElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [location]); // Re-run when location changes

  // Fetch section order from database with real-time updates
  useEffect(() => {
    const fetchSectionOrder = async () => {
      const { data, error } = await supabase
        .from("section_order")
        .select("section_name, order_position, is_visible")
        .eq("is_visible", true)
        .order("order_position", { ascending: true });

      if (data && data.length > 0) {
        // Use data from database - only visible sections
        setSections(data);
      } else if (error) {
        // Fallback only on error - exclude TikTok/Instagram by default
        setSections([
          { section_name: 'about', order_position: 1, is_visible: true },
          { section_name: 'videos', order_position: 4, is_visible: true },
          { section_name: 'blog', order_position: 5, is_visible: true },
          { section_name: 'newsletter', order_position: 6, is_visible: true },
        ]);
      } else {
        // Empty result - no sections configured, use safe defaults
        setSections([
          { section_name: 'about', order_position: 1, is_visible: true },
          { section_name: 'blog', order_position: 5, is_visible: true },
          { section_name: 'newsletter', order_position: 6, is_visible: true },
        ]);
      }
    };

    fetchSectionOrder();

    // Defer subscription setup to improve LCP
    const subscriptionTimeout = setTimeout(() => {
      // Subscribe to real-time updates for section visibility/order changes
      const channel = supabase.channel(`section-order-changes-${Math.random().toString(36).slice(2)}`, {
        config: {
          broadcast: { self: true }
        }
      });

      channel
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'section_order'
          },
          (payload) => {
            // Immediately refetch sections to reflect changes
            fetchSectionOrder();
          }
        )
        .subscribe((status) => {
        });
    }, 2000); // Defer by 2 seconds to prioritize LCP

    return () => {
      clearTimeout(subscriptionTimeout);
    };
  }, []);

  // Track page view once when component mounts (deferred)
  useEffect(() => {
    // Defer tracking to not block initial render
    const trackingTimeout = setTimeout(() => {
      if (!isAdminLoading && !isAdmin) {
        trackClick("navigation", "home page", window.location.href);
      }
    }, 500);
    
    return () => clearTimeout(trackingTimeout);
  }, [trackClick, isAdmin, isAdminLoading]);

  // Track user presence for live counter (deferred to improve LCP)
  useEffect(() => {
    // Don't track if running in iframe (Lovable preview)
    if (window.self !== window.top) {
      return;
    }

    // Wait for IP check to complete
    if (isCheckingIP) {
      return;
    }

    if (isExcludedIP) {
      return;
    }

    // Wait for admin check to complete
    if (isAdminLoading) {
      return;
    }

    if (isAdmin) {
      return; // Don't track admin users
    }

    // Skip realtime during automated audits (Lighthouse/PSI)
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/Lighthouse|Chrome-Lighthouse|Page Speed Insights/i.test(ua)) {
      return;
    }

    // Defer presence tracking to improve LCP
    const presenceTimeout = setTimeout(() => {
      const sessionId = sessionStorage.getItem("session_id");

      const channel = supabase.channel("site-visitors", {
        config: {
          presence: {
            key: sessionId || 'unknown',
          },
        },
      });

      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          const trackData = {
            online_at: new Date().toISOString(),
            page: window.location.pathname,
            session_id: sessionId,
          };
          await channel.track(trackData);
        }
      });

      // Update presence every 10 seconds
      const interval = setInterval(async () => {
        const trackData = {
          online_at: new Date().toISOString(),
          page: window.location.pathname,
          session_id: sessionId,
        };
        await channel.track(trackData);
      }, 10000);

      return () => {
        clearInterval(interval);
        supabase.removeChannel(channel);
      };
    }, 3000); // Defer by 3 seconds

    return () => {
      clearTimeout(presenceTimeout);
    };
  }, [isAdmin, isAdminLoading, isExcludedIP, isCheckingIP]);

  const renderSection = (sectionName: string) => {
    const SectionComponent = SECTION_COMPONENTS[sectionName];
    if (!SectionComponent) return null;

    // AboutSection loads immediately, others lazy load
    const isAboutSection = sectionName === 'about';
    
    return (
      <div key={sectionName}>
        {isAboutSection ? (
          <SectionComponent />
        ) : (
          <Suspense fallback={<div className="h-96" />}>
            <SectionComponent />
          </Suspense>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <SEO />
      <DebugOverlay />
      <Navigation />

      <main className="pt-16">
        <div id="home">
          <HeroSection onContactClick={() => setIsContactOpen(true)} />
        </div>

        {sections.map((section) => renderSection(section.section_name))}
      </main>

      <Footer />
      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </div>
  );
};

export default Index;
